// tslint:disable:max-line-length
import {
    Compiler,
    ComponentFactory,
    Injectable,
    Injector,
    NgModuleFactory,
    NgModuleRef, ReflectiveInjector,
    SystemJsNgModuleLoader
} from "@angular/core";
import {
    IService,
    ITCITenantComponentOptions,
    TCI_AGENT_CREATE_TYPE,
    TCITenantEnvToolsAgentsCreate,
    TCI_APP_DETAIL_ENV_MENU_TYPE,
    TCI_APP_DETAIL_MENU_TYPE,
    TCI_APP_DETAIL_MONITORING_MENU_TYPE,
    TCI_COMPONENT_MODE,
    TCI_CONNECTIONS_MENU_TYPE,
    TCITenantAppDetailEnvMenu,
    TCITenantAppDetailMonitoring,
    TCITenantAppDetailsMenu,
    TCITenantAppType,
    TCIMainMenu,
    TCIMainMenuSubroutes,
    ITCITenantComponentInput,
    TCITenantConnectionsMenu, TCITenantEnvToolsAgents, TCI_AGENT_TYPE, TCI_SUPPLEMENT_MENU_TYPE, BASE_CONSTANTS
} from "@tci/common";
import {TCITenantRemoteServices} from "./tci-tenant-remote-services";
import {Observable} from "rxjs";
import {
    ITCIAppDetailsMenuInput,
    ITCIConnectionsMenuInput,
    ITCIEnvToolsAgentCreateMenuInput, ITCIEnvToolsAgentMenuInput
} from "../ng-tenant/tci-tenant-components";
import {
    ITCIResFactory,
    TciTenantComponentCacheService,
    ITCITenantCacheOptions
} from "./tci-tenant-component-cache.service";
import {AbstractTCITenantComponent} from "./tci-tenant-component";
import { TciTenantModelService, ITCITenantComponentsPreloadMap, AbstractTciTenant } from "./tci-tenant.model";

import { cloneDeep, isBoolean } from "lodash";


interface ITciMenuData {
    menuItem: ITCITenantComponentOptions<TCI_APP_DETAIL_MENU_TYPE>;
    resFactory: ITCIResFactory<any, any>;
}

@Injectable()
export class TCITenantMenuServices {

    private preloadMapCollection: { [key: string]: ITCITenantComponentsPreloadMap } = {}; // key is tenant name
    private injectorsCollection: { [key: string]: Injector } = {}; // key is tenant name

    constructor(private loader: SystemJsNgModuleLoader,
                private compiler: Compiler,
                private tenantRemoteServices: TCITenantRemoteServices,
                private injector: Injector,
                private tciCompCacheSvc: TciTenantComponentCacheService,
                private tenantModelService: TciTenantModelService
    ) { }

    private evaluateMenuOptions<T extends TCI_APP_DETAIL_MENU_TYPE | TCI_APP_DETAIL_MONITORING_MENU_TYPE>(
        menuItems: ITCITenantComponentOptions<string | number>[],
        tenantType?: TCITenantAppType,
        input?: ITCITenantComponentInput
    ): Observable<ITCITenantComponentOptions<T>[]> {
        let preloadMap: ITCITenantComponentsPreloadMap = {};
        const tenantName = tenantType.toString();
        return this.tenantModelService.onInit()
            .map(() => {
                const tenantData = this.tenantModelService.getTenants()[tenantName];
                if (!tenantData) {
                    // console.log("Preload map not available for", tenantName);
                    return [];
                }

                // Store the preloadMap info
                preloadMap = tenantData.getTenantComponentsPreloadMap();
                this.preloadMapCollection[tenantName] = preloadMap;

                if (input && input.componentSelector) { // Check for an specific component
                    const requestedCompPreloadIndex = Object.keys(preloadMap)
                        .find(preloadSelector => preloadSelector === input.componentSelector.toString());
                    if (!requestedCompPreloadIndex) {
                        const msg = `${input.componentSelector.toString()} component is not declared at ${tenantName} tenant`;
                        // console.warn(msg);
                    } else {
                        const requestedPreload = {};
                        requestedPreload[requestedCompPreloadIndex] = preloadMap[requestedCompPreloadIndex];
                        preloadMap = requestedPreload;
                    }
                }

                return menuItems; // Returned here for "switchmap"
            })
            .flatMap(res => res)
            .flatMap(_menuCompOpt => {
                const menuCompOpt: ITCITenantComponentOptions<T> = _menuCompOpt as any;
                const compSelector = menuCompOpt.getComponentSelectorType().toString();
                if (preloadMap[compSelector]) {
                    if (input && preloadMap[compSelector].canLoadComponentFn) {
                        let serviceInjector: Injector;
                        if (tenantType) {
                            const tenantService = this.tenantModelService.getTenants()[tenantName] as AbstractTciTenant;
                            const resolvedProviders = ReflectiveInjector.resolve([
                                {
                                    provide: Injector,
                                    useValue: this.injector
                                },
                                {
                                    provide: TciTenantModelService,
                                    useValue: this.tenantModelService
                                }
                            ]);
                            serviceInjector = ReflectiveInjector.fromResolvedProviders(resolvedProviders, tenantService.getInjector());
                        }

                        const injector = serviceInjector ? serviceInjector : this.injector;
                        const bres = preloadMap[compSelector].canLoadComponentFn(menuCompOpt, input, injector);

                        // Store the injector info
                        this.injectorsCollection[tenantName] = injector;

                        const boolobs = typeof bres === "boolean" ? Observable.of(<boolean>bres) : <Observable<boolean>>bres;
                        return boolobs.map(result => ({ menuCompOpt, result }))
                            .catch(e => {
                                console.error("Tenant menu option evaluation failed:", tenantName, compSelector, e);
                                return Observable.of({ menuCompOpt, result: false });
                            });
                    } else { // Component is declared but does not need check
                        return Observable.of({ menuCompOpt, result: true });
                    }
                } else { // Component is not present in tenant module
                    // console.warn("Tenant menu option not declared:", tenantName, compSelector);
                    return Observable.of({ menuCompOpt, result: false });
                }
            })
            .filter(response => {
                // if (!isBoolean(response.result)) {
                //     console.warn("Response is not boolean", tenantName, response);
                // }
                return !!response.result;
            })
            .toArray()
            .map(res => res.map(r => r.menuCompOpt))
            .map(res => {
                const compareFn = (a: ITCITenantComponentOptions<T>, b: ITCITenantComponentOptions<T>) => {
                    if (a.getPosition() < b.getPosition()) {
                        return -1;
                    }
                    if (a.getPosition() > b.getPosition()) {
                        return 1;
                    }
                    return 0;
                };

                return res.sort(compareFn);
            });
    }

    getTenantInjector(tenantType: TCITenantAppType) {
        return this.injectorsCollection[tenantType.toString()];
    }

    getTenantPreloadMap(tenantType: TCITenantAppType): ITCITenantComponentsPreloadMap {
        return this.preloadMapCollection[tenantType.toString()];
    }

    isMigrationReadytag(metaTags: string[]): boolean {
        let isLegacyTag = false;
        if (metaTags && Array.isArray(metaTags) && metaTags.length) {
            for (let i = 0; i < metaTags.length; i++) {
                try {
                    const tag = JSON.parse(metaTags[i]);
                    if (typeof tag === "object") {
                        if (tag.tagName === BASE_CONSTANTS.LEGACY_STATE && tag.tagValue === BASE_CONSTANTS.MIGRATION_READY) {
                            isLegacyTag = true;
                            break;
                        }
                    }
                } catch (error) {
                    console.error("Unable to parse tags", error);
                    break;
                }
            }
        }
        return isLegacyTag;
    }

    /**
     * Find the menu items that are applicable for a given tenant app type
     * by checking the Preaload map provided. If "canLoadComponent" function
     * is present then execute its evaluation.
     * @param tenantType
     * @param menuType
     * @param input
     *
     * @return ITCITenantMenuOptions[] menu items are returned in a sorted order based on their position
     */
    public getAppDetailMenuItems(
        tenantType: TCITenantAppType,
        menuType?: TCI_APP_DETAIL_MENU_TYPE,
        input?: ITCIAppDetailsMenuInput
    ): Observable<ITCITenantComponentOptions<TCI_APP_DETAIL_MENU_TYPE>[]> {
        let allMenuItems: ITCITenantComponentOptions<any>[];

        if (menuType) {
            allMenuItems = TCITenantAppDetailsMenu.getAllMenuItems(menuType);
        } else {
            allMenuItems = TCITenantAppDetailsMenu.getAllMenuItems();
        }

        allMenuItems = allMenuItems.filter(item => tenantType.appliesTo(item.getTenantTypes()));
        return this.evaluateMenuOptions(allMenuItems, tenantType, input);
    }

    public getAppDetailMonitoringMenuItems(
        tenantType: TCITenantAppType,
        menuType?: TCI_APP_DETAIL_MENU_TYPE,
        input?: ITCIAppDetailsMenuInput
    ): Observable<ITCITenantComponentOptions<TCI_APP_DETAIL_MENU_TYPE>[]> {
        const allMenuItems = TCITenantAppDetailMonitoring.getAllMenuItems(menuType).filter(item => tenantType.appliesTo(item.getTenantTypes()));
        return this.evaluateMenuOptions(allMenuItems, tenantType, input);
    }

    public getAppDetailEnvMenuItems(
        tenantType: TCITenantAppType,
        menuType?: TCI_APP_DETAIL_MENU_TYPE,
        input?: ITCIAppDetailsMenuInput
    ): Observable<ITCITenantComponentOptions<TCI_APP_DETAIL_MENU_TYPE>[]> {
        const allMenuItems = TCITenantAppDetailEnvMenu.getAllMenuItems(menuType).filter(item => tenantType.appliesTo(item.getTenantTypes()));
        return this.evaluateMenuOptions(allMenuItems, tenantType, input);
    }

    public getEnvToolsMenuItems(
        menuItem: ITCITenantComponentOptions<string | number>,
        input: ITCITenantComponentInput
    ): Observable<ITCITenantComponentOptions<TCI_APP_DETAIL_MENU_TYPE>[]> {
        const obs = menuItem.getTenantTypes().map(type => {
            const _input = cloneDeep(input);
            input.tenantAppType = type;
            return this.evaluateMenuOptions([menuItem], input.tenantAppType, _input);
        });

        return Observable.merge(...obs)
            .filter(res => !!res || res.length === 0)
            .toArray()
            .map(res => [].concat.apply([], res));
    }

    public getConnectionsMenuItems(
        tenantType: TCITenantAppType,
        filter: (v: TCITenantConnectionsMenu) => boolean,
        input: ITCIConnectionsMenuInput,
    ): Observable<ITCITenantComponentOptions<TCI_APP_DETAIL_MENU_TYPE>[]> {
        const allMenuItems = TCITenantConnectionsMenu.getAllMenuItems(filter).filter(item => tenantType.appliesTo(item.getTenantTypes()));
        return this.evaluateMenuOptions(allMenuItems, tenantType, input);
    }

    public getEnvToolsAgentMenuItems(
        menuItem: ITCITenantComponentOptions<string | number>,
        input: ITCITenantComponentInput
    ): Observable<ITCITenantComponentOptions<TCI_APP_DETAIL_MENU_TYPE>[]> {
        const obs = menuItem.getTenantTypes().map(type => {
            const _input = cloneDeep(input);
            input.tenantAppType = type;
            return this.evaluateMenuOptions([menuItem], input.tenantAppType, _input);
        });

        return Observable.merge(...obs)
            .filter(res => !!res || res.length === 0)
            .toArray()
            .map(res => [].concat.apply([], res));
    }

    public getEnvToolsAgentCreateMenuItems(
        menuItem: ITCITenantComponentOptions<string | number>,
        input: ITCITenantComponentInput
    ): Observable<ITCITenantComponentOptions<TCI_APP_DETAIL_MENU_TYPE>[]> {
        const obs = menuItem.getTenantTypes().map(type => {
            const _input = cloneDeep(input);
            input.tenantAppType = type;
            return this.evaluateMenuOptions([menuItem], input.tenantAppType, _input);
        });

        return Observable.merge(...obs)
            .filter(res => !!res || res.length === 0)
            .flatMap(res => res)
            .distinct(res => res.getComponentSelectorType())
            .toArray()
            .map(res => [].concat.apply([], res));
    }

    public getEnvToolsSupplementsMenuItems(
        menuItem: ITCITenantComponentOptions<string | number>,
        input: ITCITenantComponentInput
    ): Observable<ITCITenantComponentOptions<TCI_SUPPLEMENT_MENU_TYPE>[]> {
        const obs = menuItem.getTenantTypes().map(type => {
            const _input = cloneDeep(input);
            input.tenantAppType = type;
            return this.evaluateMenuOptions([menuItem], input.tenantAppType, _input);
        });

        return Observable.merge(...obs)
            .filter(res => !!res || res.length === 0)
            .toArray()
            .map(res => [].concat.apply([], res));
    }

    /**
     * lists menu items without looking at the cache for its presence
     * @param tenantAppType
     * @param menuType
     */
    // public listAppDetailMenuItems(
    //     tenantAppType: TCITenantAppType,
    //     menuType: TCI_APP_DETAIL_ENV_MENU_TYPE): ITCITenantComponentOptions<TCI_APP_DETAIL_MENU_TYPE>[] {
    //     const allMenuItems = TCITenantAppDetailsMenu.getAllMenuItems(menuType).filter(item => tenantAppType.appliesTo(item.getTenantTypes()));
    //     const compareFn = (a: ITCITenantComponentOptions<TCI_APP_DETAIL_ENV_MENU_TYPE>, b: ITCITenantComponentOptions<TCI_APP_DETAIL_ENV_MENU_TYPE>) => {
    //         if (a.getPosition() < b.getPosition()) {
    //             return -1;
    //         }
    //         if (a.getPosition() > b.getPosition()) {
    //             return 1;
    //         }
    //         return 0;
    //     };
    //     return allMenuItems.sort(compareFn);
    // }

    /**
     * Find the menu items that are applicable for a given tenant app type
     * Each tenant-svc is queried to find which menu items are being implemented by a
     * component based on selector ITCITenantMenu.getComponentSelectorType()
     * @param tenantAppType
     * @param menuType
     * @param input?
     *
     * @return ITCITenantMenuOptions[] menu items are returned in a sorted order based on their position
     */
    public old_getAppDetailEnvMenuItems<T extends ITCIAppDetailsMenuInput>(
        tenantAppType: TCITenantAppType,
        menuType: TCI_APP_DETAIL_ENV_MENU_TYPE,
        input?: T):
        Observable<ITCITenantComponentOptions<TCI_APP_DETAIL_ENV_MENU_TYPE>[]> {
        const allMenuItems = TCITenantAppDetailEnvMenu.getAllMenuItems(menuType)
            .filter(item => tenantAppType.appliesTo(item.getTenantTypes()));
        const compareFn = (a: ITCITenantComponentOptions<TCI_APP_DETAIL_ENV_MENU_TYPE>, b: ITCITenantComponentOptions<TCI_APP_DETAIL_ENV_MENU_TYPE>) => {
            if (a.getPosition() < b.getPosition()) {
                return -1;
            }
            if (a.getPosition() > b.getPosition()) {
                return 1;
            }
            return 0;
        };
        if (input) {
            if (!input.application) {
                throw new Error("Application missing in input");
            }
            if (!input.application.type) {
                throw new Error("TenantAppType missing in input");
            }
        }
        const cacheOptions = {
            routeId: TCIMainMenu.MAIN_MENU_APPLICATIONS.getRouteId(),
            // TODO: @rzavala  need to find out if this function is being called in app list or app details
            subrouteId: TCIMainMenuSubroutes.TCI_APPLICATION_APP_DETAILS.getSubRouteId()
        };
        return this.tciCompCacheSvc.getCache(false, cacheOptions)
            .map(() => allMenuItems)
            .flatMap(items => items)
            .switchMap(menuItem =>
                this.tciCompCacheSvc.getComponent(tenantAppType, menuItem.getComponentSelectorType())
                    .map(res => res),
                (item, resFactory, x, y) => {

                    // Set the missing component data to the input object
                    input.componentSelector = item.getComponentSelectorType();
                    input.subMenuLeftRouteId = item.getRouteId();

                    if (resFactory) {
                        return <ITciMenuData>{
                            menuItem: item, resFactory: resFactory
                        };
                    } else {
                        return null;
                    }

                }
            )
            .filter(data => !!data)
            .filter(data => !!data.menuItem && !!data.resFactory)
            .filter(data => !input.componentSelector || data.menuItem.getComponentSelectorType() === input.componentSelector)
            .filter(data => data.menuItem.getTenantTypes().some(type => type.appliesTo(input.tenantAppType.getPseudoTenants())))
            .map(data => {
                if (input &&
                    input.application.type.equals(data.resFactory.tenantType) &&
                    data.resFactory.cmpFactory.componentType.prototype instanceof AbstractTCITenantComponent &&
                    data.resFactory.cmpFactory.componentType.prototype.getAnnotations() &&
                    data.resFactory.cmpFactory.componentType.prototype.getAnnotations().canLoadComponent) {
                    const modInjector = (<NgModuleRef<any>>data.resFactory.ngModFactory.create(this.injector)).injector;
                    try {
                        const bres = data.resFactory.cmpFactory.componentType.prototype.getAnnotations()
                            .canLoadComponent(data.menuItem, input, modInjector);
                        const boolobs = typeof bres === "boolean"
                            ? Observable.of(<boolean>bres)
                            : <Observable<boolean>>bres;
                        return <Observable<ITciMenuData>>Observable.create(obs1 => {
                            boolobs
                                .catch(err => {
                                    console.error(
                                        `Tenant.canLoadComponent Error for tenant: ${data.resFactory.tenantType} and menuItem: ${data.menuItem.getComponentSelectorType().toString()}.`, err);
                                    return Observable.of(false);
                                })
                                .subscribe(bresult => {
                                    // data.canLoadComponent = bresult;
                                    if (bresult) {
                                        obs1.next(<ITciMenuData>data);
                                        obs1.complete();
                                    } else {
                                        obs1.next(null);
                                        obs1.complete();
                                    }
                                });
                        });
                    } catch (e) {
                        console.log("canLoadComponent() failed for menu item:" + data.menuItem.getComponentSelectorType().toString());
                        console.log(e);
                        return Observable.of(null);
                    }
                } else {
                    // data.canLoadComponent = true;
                    return Observable.of(<ITciMenuData>data);
                }
            })
            .flatMap(res => res)
            .filter(res => !!res)
            .map(res => res.menuItem)
            .toArray()
            .map(data => data.sort(compareFn));
    }

    /**
     * Find the menu items that are applicable for a given tenant app type
     * Each tenant-svc is queried to find which menu items are being implemented by a
     * component based on selector ITCITenantMenu.getComponentSelectorType()
     * @param tenantAppType
     * @param menuType
     * @param input?
     *
     * @return ITCITenantMenuOptions[] menu items are returned in a sorted order based on their position
     */
    public old_getAppDetailMonitoringMenuItems<T extends ITCIAppDetailsMenuInput>(
        tenantAppType: TCITenantAppType,
        menuType: TCI_APP_DETAIL_MONITORING_MENU_TYPE,
        input?: T):
        Observable<ITCITenantComponentOptions<TCI_APP_DETAIL_MONITORING_MENU_TYPE>[]> {
        const allMenuItems = TCITenantAppDetailMonitoring.getAllMenuItems(menuType).filter(item => tenantAppType.appliesTo(item.getTenantTypes()));
        const compareFn = (a: ITCITenantComponentOptions<TCI_APP_DETAIL_MONITORING_MENU_TYPE>, b: ITCITenantComponentOptions<TCI_APP_DETAIL_ENV_MENU_TYPE>) => {
            if (a.getPosition() < b.getPosition()) {
                return -1;
            }
            if (a.getPosition() > b.getPosition()) {
                return 1;
            }
            return 0;
        };
        if (input) {
            if (!input.application) {
                throw new Error("Application missing in input");
            }
            if (!input.application.type) {
                throw new Error("TenantAppType missing in input");
            }
        }
        const cacheOptions: ITCITenantCacheOptions = {
            routeId: TCIMainMenu.MAIN_MENU_APPLICATIONS.getRouteId(),
            // TODO: @rzavala  need to find out if this function is being called in app list or app details
            subRouteId: TCIMainMenuSubroutes.TCI_APPLICATION_APP_DETAILS.getSubRouteId()
        };
        return this.tciCompCacheSvc.getCache(false, cacheOptions)
            .map(() => allMenuItems)
            .flatMap(items => items)
            .switchMap(menuItem =>
                this.tciCompCacheSvc.getComponent(tenantAppType, menuItem.getComponentSelectorType())
                    .map(res => res),
                (item, resFactory, x, y) => {
                    if (resFactory) {
                        return <ITciMenuData>{
                            menuItem: item, resFactory: resFactory
                        };
                    } else {
                        return null;
                    }

                }
            )
            .filter(data => !!data)
            .filter(data => !!data.menuItem && !!data.resFactory)
            .filter(data => !input.componentSelector || data.menuItem.getComponentSelectorType() === input.componentSelector)
            .filter(data => input.tenantAppType.appliesTo(data.menuItem.getTenantTypes()))
            .map(data => {
                if (input &&
                    input.application.type.equals(data.resFactory.tenantType) &&
                    data.resFactory.cmpFactory.componentType.prototype instanceof AbstractTCITenantComponent &&
                    data.resFactory.cmpFactory.componentType.prototype.getAnnotations() &&
                    data.resFactory.cmpFactory.componentType.prototype.getAnnotations().canLoadComponent) {
                    const modInjector = (<NgModuleRef<any>>data.resFactory.ngModFactory.create(this.injector)).injector;
                    try {
                        const bres = data.resFactory.cmpFactory.componentType.prototype.getAnnotations()
                            .canLoadComponent(data.menuItem, input, modInjector);
                        const boolobs = typeof bres === "boolean"
                            ? Observable.of(<boolean>bres)
                            : <Observable<boolean>>bres;
                        return <Observable<ITciMenuData>>Observable.create(obs1 => {
                            boolobs
                                .catch(err => {
                                    console.error(
                                        `Tenant.canLoadComponent Error for tenant: ${data.resFactory.tenantType} and menuItem: ${data.menuItem.getComponentSelectorType().toString()}.`, err);
                                    return Observable.of(false);
                                })
                                .subscribe(bresult => {
                                    // data.canLoadComponent = bresult;
                                    if (bresult) {
                                        obs1.next(<ITciMenuData>data);
                                        obs1.complete();
                                    } else {
                                        obs1.next(null);
                                        obs1.complete();
                                    }
                                });
                        });
                    } catch (e) {
                        console.log("canLoadComponent() failed for menu item:" + data.menuItem.getComponentSelectorType().toString());
                        console.log(e);
                        return Observable.of(null);
                    }
                } else {
                    // data.canLoadComponent = true;
                    return Observable.of(<ITciMenuData>data);
                }
            })
            .flatMap(res => res)
            .filter(res => !!res)
            .map(res => res.menuItem)
            .toArray()
            .map(data => data.sort(compareFn));
    }

    /**
     * Find the menu items that are applicable for a given tenant app type
     * Each tenant-svc is queried to find which menu items are being implemented by a
     * component based on selector ITCITenantMenu.getComponentSelectorType()
     * @param tenantAppType
     * @param menuType
     * @param input?
     *
     * @return ITCITenantMenuOptions[] menu items are returned in a sorted order based on their position
     */
    // public getAppDetailMenuItemsOld<T extends ITCIAppDetailsMenuInput>(
    //     tenantAppType: TCITenantAppType,
    //     menuType: TCI_APP_DETAIL_MENU_TYPE,
    //     input?: T):
    //     Observable<ITCITenantComponentOptions<TCI_APP_DETAIL_MENU_TYPE>[]> {
    //     let _modFactory: NgModuleFactory<any>;
    //     const _thisObj = this;
    //     const allMenuItems = TCITenantAppDetailsMenu.getAllMenuItems(menuType);
    //     const compareFn = (a: ITCITenantComponentOptions<TCI_APP_DETAIL_MENU_TYPE>, b: ITCITenantComponentOptions<TCI_APP_DETAIL_MENU_TYPE>) => {
    //         if (a.getPosition() < b.getPosition()) {
    //             return -1;
    //         }
    //         if (a.getPosition() > b.getPosition()) {
    //             return 1;
    //         }
    //         return 0;
    //     };
    //
    //     return this.tenantRemoteServices.getRemoteServices()
    //         .flatMap(svc => svc)
    //         .filter((tenant: IService) => {
    //             const cfg = (<any>SystemJS).getConfig();
    //             const svcName = tenantAppType.getSvcName();
    //             return svcName === TCITenantAppType.fromString(tenant.name).getSvcName() && cfg.map && cfg.packages && cfg.map[svcName];
    //         })
    //         .map(() => Observable.fromPromise(this.loader.load(tenantAppType.getSvcName())))
    //         .flatMap(modFac => modFac)
    //         .map(modFactory => {
    //             _modFactory = modFactory;
    //             return Observable.fromPromise(_thisObj.compiler.compileModuleAndAllComponentsAsync<any>(modFactory.moduleType));
    //         })
    //         .flatMap(obsfactory => obsfactory)
    //         // .map(mwcfactories => mwcfactories.componentFactories)
    //         .switchMap(mwcfactories => Observable.of(...mwcfactories.componentFactories), (ngModFactory, cmpFactory) => ({
    //             ngModFactory,
    //             cmpFactory
    //         }))
    //         // .flatMap(factories => factories)
    //         .switchMap(fac => Observable.of(...allMenuItems), (fac, menuItem, x, y) => {
    //             const canLoadComponent = false;
    //             const cmpFactory = fac.cmpFactory;
    //             const modInjector = (<NgModuleRef<any>>fac.ngModFactory.ngModuleFactory.create(this.injector)).injector;
    //             return (
    //                 { cmpFactory, modInjector, menuItem, canLoadComponent }
    //             );
    //         }
    //         )
    //         .filter(data =>
    //             data.cmpFactory.componentType.prototype instanceof AbstractTCITenantComponent
    //             && data.cmpFactory.componentType.prototype.getAnnotations()
    //             && data.cmpFactory.selector === data.menuItem.getComponentSelectorType().toString()
    //         )
    //         .map(data => {
    //             if (input &&
    //                 data.cmpFactory.componentType.prototype instanceof AbstractTCITenantComponent &&
    //                 data.cmpFactory.componentType.prototype.getAnnotations() &&
    //                 data.cmpFactory.componentType.prototype.getAnnotations().canLoadComponent) {
    //                 const bres = data.cmpFactory.componentType.prototype.getAnnotations().canLoadComponent(data.menuItem, input, data.modInjector);
    //                 const boolobs = typeof bres === "boolean"
    //                     ? Observable.of(<boolean>bres)
    //                     : <Observable<boolean>>bres;
    //                 return <Observable<{ cmpFactory: ComponentFactory<any>, menuItem: ITCITenantComponentOptions<any>, canLoadComponent: boolean }>>
    //                     Observable.create(obs => {
    //                         boolobs.subscribe(bresult => {
    //                             data.canLoadComponent = bresult;
    //                             obs.next(data);
    //                             obs.complete();
    //                         });
    //                     });
    //
    //             } else {
    //                 data.canLoadComponent = true;
    //                 return Observable.of(data);
    //             }
    //         })
    //         .flatMap(data => data)
    //         // .map(cmpFactory => allMenuItems.filter(val => {
    //         //     let canloadComponent = true;
    //         //     if (
    //         //         input &&
    //         //         cmpFactory.componentType.prototype instanceof AbstractTCITenantComponent &&
    //         //         cmpFactory.componentType.prototype.getAnnotations() &&
    //         //         cmpFactory.componentType.prototype.getAnnotations().canLoadComponent
    //         //     ) {
    //         //         canloadComponent = cmpFactory.componentType.prototype.getAnnotations()
    //         //             .canLoadComponent(val, input);
    //         //     }
    //         //     return (cmpFactory.componentType.prototype instanceof AbstractTCITenantComponent
    //         //         && cmpFactory.componentType.prototype.getAnnotations()
    //         //         && cmpFactory.selector === val.getComponentSelectorType().toString()
    //         //         && canloadComponent);
    //         // }))
    //         // .flatMap(arr => arr)
    //         // .distinct( (data) => data.getMenuOption())
    //         .filter(data => data.canLoadComponent)
    //         .map(data => data.menuItem)
    //         .toArray()
    //         .map(data => data.sort(compareFn));
    // }

    public old_getConnectionsMenuItems<T extends ITCIConnectionsMenuInput>(
        tenantAppType: TCITenantAppType, filter: (v: TCITenantConnectionsMenu) => boolean, input?: T):
        Observable<ITCITenantComponentOptions<TCI_CONNECTIONS_MENU_TYPE>[]> {
        const allMenuItems = TCITenantConnectionsMenu.getAllMenuItems(filter).filter(item => tenantAppType.appliesTo(item.getTenantTypes()));
        const compareFn = (a: ITCITenantComponentOptions<TCI_APP_DETAIL_MENU_TYPE>, b: ITCITenantComponentOptions<TCI_APP_DETAIL_MENU_TYPE>) => {
            if (a.getPosition() < b.getPosition()) {
                return -1;
            }
            if (a.getPosition() > b.getPosition()) {
                return 1;
            }
            return 0;
        };
        if (input) {
            if (!input.connection) {
                throw new Error("Connection missing in input");
            }
            if (!input.connection.tenantType) {
                throw new Error("TenantAppType missing in input");
            }
        }
        const cacheOptions: ITCITenantCacheOptions = { routeId: TCIMainMenu.MAIN_MENU_CONNECTIONS.getRouteId() };
        return this.tciCompCacheSvc.getCache(false, cacheOptions)
            .map(() => allMenuItems)
            .flatMap(items => items)
            .switchMap(menuItem =>
                this.tciCompCacheSvc.getComponent(tenantAppType, menuItem.getComponentSelectorType())
                    .map(res => res), (menuItem, resFactory) => resFactory ? <ITciMenuData>{ menuItem, resFactory } : null
            )
            .filter(data => !!data)
            .filter(data => !!data.menuItem && !!data.resFactory)
            .filter(data => !input.componentSelector || data.menuItem.getComponentSelectorType() === input.componentSelector)
            .filter(data => data.menuItem.getTenantTypes().map(tenant => tenant.toString()).includes(input.tenantAppType.toString()))
            .map(data => {
                if (input &&
                    input.connection.tenantType.equals(data.resFactory.tenantType) &&
                    data.resFactory.cmpFactory.componentType.prototype instanceof AbstractTCITenantComponent &&
                    data.resFactory.cmpFactory.componentType.prototype.getAnnotations() &&
                    data.resFactory.cmpFactory.componentType.prototype.getAnnotations().canLoadComponent) {
                    const modInjector = (<NgModuleRef<any>>data.resFactory.ngModFactory.create(this.injector)).injector;
                    try {
                        const bres = data.resFactory.cmpFactory.componentType.prototype.getAnnotations()
                            .canLoadComponent(data.menuItem, input, modInjector);
                        const boolobs = typeof bres === "boolean"
                            ? Observable.of(<boolean>bres)
                            : <Observable<boolean>>bres;
                        return <Observable<ITciMenuData>>Observable.create(obs1 => {
                            boolobs
                                .catch(err => {
                                    console.error(
                                        `Tenant.canLoadComponent Error for tenant: ${data.resFactory.tenantType} and menuItem: ${data.menuItem.getComponentSelectorType().toString()}.`, err);
                                    return Observable.of(false);
                                })
                                .subscribe(bresult => {
                                    if (bresult) {
                                        obs1.next(<ITciMenuData>data);
                                        obs1.complete();
                                    } else {
                                        obs1.next(null);
                                        obs1.complete();
                                    }
                                });
                        });
                    } catch (e) {
                        console.log("canLoadComponent() failed for menu item:" + data.menuItem.getComponentSelectorType().toString());
                        console.log(e);
                        return Observable.of(null);
                    }
                } else {
                    return Observable.of(<ITciMenuData>data);
                }
            })
            .flatMap(res => res)
            .filter(res => !!res)
            .map(res => res.menuItem)
            .toArray()
            .map(data => data.sort(compareFn));
    }

}
